/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import ThumbnailOverlay from '@/components/Carousel/ThumbnailComponent/ThumbnailOverlay';
import { Grid } from '@/components/Grid';
import NextImage from '@/components/NextImage';
import { Rating } from '@/components/Rating/Rating';
import { useFeatureFlag } from '@/features/globalConfig';
import { useLabels } from '@/hooks/useLabels';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Actionable, Skeleton, Text, View } from '@az/starc-ui';
import cx from 'classnames';
import { type ActiveImage } from '../../hooks/useReviewImageModal';
import { SyndicatedReviewBadge } from '../SyndicatedReviewBadge/SyndicatedReviewBadge';
import { ReviewFeedbackActions } from '../ReviewFeedbackActions/ReviewFeedbackActions';
import type { ProductReview } from '../../interface';
import styles from './styles.module.scss';

export type CustomerReviewProps = {
  review: ProductReview;
  onImageClick?: (image: ActiveImage) => void;
  className?: string;
  reviewContentClassName?: string;
  authorReviewsCount: number | undefined;
  authorVotesCount: number | undefined;
};

const labelMap = {
  yes: 'label_reviews_yes',
  no: 'label_reviews_no',
  recommendsProduct: 'label_reviews_recommendsProduct',
  incentivizedReview: 'label_reviews_incentivizedReview',
  verifiedPurchaser: 'label_reviews_verifiedPurchaser',
  anonymous: 'label_reviews_anonymous',
  seeMore: 'label_reviews_seeMore',
  authorReviews: 'label_reviews_authorReviews',
  authorVotes: 'label_reviews_authorVotes',
  customerImageAltFallback: 'label_reviews_customerImageAltFallback',
};

export function CustomerReview({
  review,
  className,
  reviewContentClassName,
  authorReviewsCount,
  authorVotesCount,
  onImageClick,
}: CustomerReviewProps) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const labels = useLabels(labelMap);
  const bvAPIEnabled = useFeatureFlag('BV_API_ENABLED') === 'true';

  const maxImageCount = isMobile ? 4 : 6;

  return (
    <View className={cx(styles.reviewContainer, className)}>
      <View className={styles.reviewHeader}>
        <View className={styles.reviewInformation}>
          <View>
            <View className={styles.starRating} attributes={{ 'data-testid': 'rating' }}>
              <Rating value={review.rating} />
            </View>
          </View>

          <View>
            <Text
              as="span"
              weight="bold"
              className={styles.reviewTitle}
              attributes={{ 'data-testid': 'title' }}
            >
              {review.title}
            </Text>
          </View>
          <View>
            <Text
              as="span"
              className={styles.reviewSubtitle}
              attributes={{ 'data-testid': 'reviewer' }}
            >{`${review.reviewer ?? labels.anonymous} - ${review.timeAgo}`}</Text>
          </View>
        </View>

        <View className={styles.badgeContainer} attributes={{ 'data-testid': 'badges' }}>
          {review.incentivizedReview && (
            <View className={styles.reviewBadge}>
              <NextImage src="/images/reviews/gift.svg" width={24} height={24} />
              <Text as="span">{labels.incentivizedReview}</Text>
            </View>
          )}
          {review.verifiedPurchase && (
            <View className={styles.reviewBadge}>
              <NextImage src="/images/reviews/star-circle.svg" width={24} height={24} />
              <Text as="span">{labels.verifiedPurchaser}</Text>
            </View>
          )}
        </View>
      </View>

      <View>
        <Grid container className={cx(styles.reviewContent, reviewContentClassName)}>
          <Grid item sm={12} md={12} lg={6}>
            <View>
              <Text as="p" attributes={{ 'data-testid': 'body' }}>
                {review.body}
              </Text>
            </View>
          </Grid>

          <Grid item sm={12} md={12} lg={6}>
            <ul className={styles.contentList}>
              {!!review.vehicleQuestion && (
                <li>
                  <View>
                    <Text as="p" attributes={{ 'data-testid': 'vehicle' }}>
                      <strong>{review.vehicleQuestion}</strong> {review.vehicleAnswer}
                    </Text>
                  </View>
                </li>
              )}
              <li>
                <View>
                  <Text as="p" attributes={{ 'data-testid': 'recommends' }}>
                    <strong>{labels.recommendsProduct}</strong>{' '}
                    {review.recommend ? labels.yes : labels.no}
                  </Text>
                </View>
              </li>

              {!!review.profileId && bvAPIEnabled && (
                <li>
                  <ul className={styles.authorStatsRow}>
                    <li aria-label={`${labels.authorReviews} ${authorReviewsCount ?? ''}`}>
                      <Text as="span">
                        <strong>{labels.authorReviews}</strong>{' '}
                      </Text>
                      {authorReviewsCount !== undefined ? (
                        <Text as="span">{authorReviewsCount}</Text>
                      ) : (
                        <Skeleton width="30px" borderRadius="small" />
                      )}
                    </li>
                    <li aria-label={`${labels.authorVotes} ${authorVotesCount ?? ''}`}>
                      <Text as="span">
                        <strong>{labels.authorVotes}</strong>{' '}
                      </Text>
                      {authorVotesCount !== undefined ? (
                        <Text as="span">{authorVotesCount}</Text>
                      ) : (
                        <Skeleton width="30px" borderRadius="small" />
                      )}
                    </li>
                  </ul>
                </li>
              )}

              {bvAPIEnabled && !review.syndicatedReviewFlag && (
                <li>
                  <ReviewFeedbackActions
                    reviewId={review.id}
                    positiveFeedbackCount={review.positiveFeedbackCount}
                    negativeFeedbackCount={review.negativeFeedbackCount}
                  />
                </li>
              )}

              {!!review.syndicatedReviewFlag && (
                <li>
                  <SyndicatedReviewBadge
                    name={review.syndicatedClientName}
                    logo={review.syndicatedClientLogoUrl}
                  />
                </li>
              )}

              {!!review.images.length && (
                <li>
                  <View
                    direction="row"
                    className={styles.imagesRow}
                    attributes={{ 'data-testid': 'images' }}
                  >
                    {review.images.slice(0, maxImageCount).map((image, i) => (
                      <View key={image.id} className={styles.gridItem}>
                        <Actionable
                          className={styles.reviewImageWrapper}
                          onClick={() => {
                            onImageClick?.({ imageId: image.id, reviewId: review.id });
                          }}
                        >
                          {i + 1 === maxImageCount && review.images.length > maxImageCount && (
                            <ThumbnailOverlay text={labels.seeMore} />
                          )}
                          <NextImage
                            src={image.thumbPhotoUrl}
                            width={66}
                            height={66}
                            className={styles.reviewImage}
                            alt={image.photoCaption ?? labels.customerImageAltFallback}
                          />
                        </Actionable>
                      </View>
                    ))}
                  </View>
                </li>
              )}
            </ul>
          </Grid>
        </Grid>
      </View>
    </View>
  );
}
